import moment from 'moment-mini'
import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  first,
  map,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Member } from 'src/app/features/member/member'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { scrollToFirstInvalidControl } from 'src/app/shared/utils'
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { EmployeeContract } from '../../employee-contract/employee-contract'
import { employeeContractActions } from '../../employee-contract/employee-contract.actions'
import { employeeContractSelectors } from '../../employee-contract/employee-contract.selectors'
import { supervisorSkillActions } from '../../supervisor-skill/supervisor-skill.actions'
import { EmployeeBooking, EmployeeBookingTypes } from '../employee-booking'
import { employeeBookingActions } from '../employee-booking.actions'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { environment } from 'src/environments/environment'
import { employeeContractManagerLinkSelectors } from '../../employee-contract-manager-link/employee-contract-manager-link.selectors'
import { EmployeeBookingService } from '../employee-booking.service'
import { supervisorSkillSelectors } from '../../supervisor-skill/supervisor-skill.selectors'
import { EmployeeContractService } from '../../employee-contract/employee-contract.service'
import { TranslateService } from '@ngx-translate/core'
import { ConfigOptionsKeys } from '../../../../shared/constants'

type EmployeeBookingConfig = {
  typeEditable: boolean
  productEditable: boolean
  startTimeEditable: boolean
  endTimeEditable: boolean
  contractEditable: boolean
  descriptionVisible: boolean
  descriptionEditable: boolean
  descriptionRequired: boolean
  additionalInfoVisible: boolean
  additionalInfoEditable: boolean
  additionalInfoRequired: boolean
  cancelBookingActive: boolean
  bookingTypes: EmployeeBookingTypes[]
}

@UntilDestroy()
@Component({
  selector: 'employee-booking-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class EmployeeBookingFormComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<unknown>()
  @Output() onCancel = new EventEmitter<boolean>()
  @Input() employeeBooking: EmployeeBooking
  @Input() disableSaveButton$: Observable<boolean>
  @Input() bookingIsAlreadyChecked: boolean = false
  @Input() hasOnlyTrainerRole: boolean = false

  public disableSaveButton: boolean = false
  public isLoading$: Observable<boolean>
  public member$: Observable<Member>
  public contracts$: Observable<EmployeeContract[]>
  public bookingTypes$: Observable<EmployeeBookingTypes[]>
  public employeeBookingTypes = EmployeeBookingTypes
  public productEntities$: Observable<Dictionary<Product>>
  public contractEntities$: Observable<Dictionary<EmployeeContract>>
  public contractIsNotValidOnEndDate: boolean = false
  public contractIsNotValidOnStartDate: boolean = false
  public selectedType: EmployeeBookingTypes
  public availableProductsForEmployee$: Observable<Product[]>
  public form: UntypedFormGroup
  public messageForm: UntypedFormGroup
  public showConfirm: boolean = false
  public employeeBookingConfig: EmployeeBookingConfig = {
    typeEditable: false,
    productEditable: false,
    startTimeEditable: false,
    endTimeEditable: false,
    contractEditable: false,
    descriptionVisible: false,
    descriptionEditable: false,
    descriptionRequired: false,
    additionalInfoVisible: false,
    additionalInfoEditable: false,
    additionalInfoRequired: false,
    cancelBookingActive: false,
    bookingTypes: []
  }
  public employeeBookingConfig$: Observable<EmployeeBookingConfig>

  private employeeModule = environment.moduleConfigs.employeeModule

  constructor(
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private actions$: Actions,
    private employeeBookingService: EmployeeBookingService,
    private employeeContractService: EmployeeContractService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(
      supervisorSkillSelectors.selectIsLoading
    )

    this.form = this.fb.group({
      productId: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      type: [null, [Validators.required]],
      contractId: [null, [Validators.required]],
      description: [null, []],
      memo: [null, []]
    })
    this.disableSaveButton$.pipe(untilDestroyed(this)).subscribe((disable) => {
      this.disableSaveButton = disable
    })
    this.messageForm = this.fb.group({
      message: [null, []]
    })

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.contractEntities$ = this.store.select(
      employeeContractSelectors.selectEntities
    )

    this.member$ = this.store.select(authSelectors.selectMember)

    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        employeeContractActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              $and: [
                {
                  personId: member.id,
                  $or: [
                    {
                      endDate: {
                        [CondOperator.GREATER_THAN]: moment().toISOString()
                      }
                    },
                    {
                      endDate: {
                        [CondOperator.EQUALS]: null
                      }
                    }
                  ]
                }
              ]
            }
          })
        })
      )
    })

    this.contracts$ = combineLatest([
      this.store.select(employeeContractManagerLinkSelectors.selectEntities),
      this.store.select(employeeContractSelectors.selectAll),
      this.member$,
      this.form.valueChanges
    ]).pipe(
      map(
        ([
          employeeContractManagerLinkEntities,
          contracts,
          member,
          valueChanges
        ]) => {
          const availableContracts =
            this.employeeContractService.getAvailableContracts(
              contracts,
              employeeContractManagerLinkEntities,
              member,
              valueChanges.endDate
            )

          return this.employeeContractService.getAvailableContracts(
            contracts,
            employeeContractManagerLinkEntities,
            member,
            valueChanges.endDate
          )
        }
      )
    )

    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        supervisorSkillActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { personId: member.id },
            join: [{ field: 'product' }, { field: 'product.translations' }]
          })
        })
      )
    })

    this.availableProductsForEmployee$ = combineLatest([
      this.store.select(supervisorSkillSelectors.selectAll),
      this.store.select(productSelectors.selectEntities),
      this.member$
    ]).pipe(
      map(([allSupervisorSkills, productEntities, member]) => {
        const supervisorSkillsOfMember = allSupervisorSkills.filter(
          (supervisorSkill) =>
            productEntities[supervisorSkill.productId]?.isActive &&
            productEntities[supervisorSkill.productId]?.isEmployeeAbility &&
            supervisorSkill.personId == member.id
        )

        return supervisorSkillsOfMember.map(
          (supervisorSkill) => productEntities[supervisorSkill.productId]
        )
      })
    )

    this.form.controls.type.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((selectedType) => {
        this.selectedType = selectedType
        if (selectedType == EmployeeBookingTypes.UNAVAILABLE) {
          this.form.controls.productId.setValidators([])
          this.form.controls.productId.clearValidators()
          this.form.controls.productId.updateValueAndValidity()
          this.form.controls.contractId.setValidators([])
          this.form.controls.contractId.clearValidators()
          this.form.controls.contractId.updateValueAndValidity()
          this.form.controls.description.setValidators([])
          this.form.controls.description.clearValidators()
          this.form.controls.description.updateValueAndValidity()
          this.form.controls.memo.setValidators([])
          this.form.controls.memo.clearValidators()
          this.form.controls.memo.updateValueAndValidity()
        } else {
          this.form.controls.productId.setValidators([Validators.required])
          this.form.controls.productId.updateValueAndValidity()
          this.form.controls.contractId.setValidators([Validators.required])
          this.form.controls.contractId.updateValueAndValidity()
        }
      })

    this.bookingTypes$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.EMPLOYEE_BOOKING
      )
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        map((employeeBookingConfigRaw) => {
          const bookingTypes =
            (
              JSON.parse(employeeBookingConfigRaw.value) as {
                bookingTypes?: number[]
              }
            )?.bookingTypes ?? []
          if (this.hasOnlyTrainerRole || !this.employeeModule?.isActive) {
            this.form.controls.type.patchValue(EmployeeBookingTypes.UNAVAILABLE)

            return bookingTypes.filter(
              (bookingType) => bookingType == EmployeeBookingTypes.UNAVAILABLE
            )
          }
          return bookingTypes
        })
      )

    this.employeeBookingConfig$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.EMPLOYEE_BOOKING
      )
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        map((employeeBookingConfigRaw) => {
          try {
            const config = JSON.parse(employeeBookingConfigRaw.value)
            this.employeeBookingConfig = config
            this.updateControlEditability('type', config.typeEditable)
            this.updateControlEditability('productId', config.productEditable)
            this.updateControlEditability('startDate', config.startTimeEditable)
            this.updateControlEditability('endDate', config.endTimeEditable)
            this.updateControlEditability('contractId', config.contractEditable)
            this.updateControlEditability(
              'description',
              config.descriptionEditable
            )
            this.updateControlEditability('memo', config.additionalInfoEditable)

            if (config.descriptionRequired) {
              this.form.controls.description.setValidators([
                Validators.required
              ])
              this.form.controls.description.updateValueAndValidity({
                emitEvent: false
              })
            }
            if (config.additionalInfoRequired) {
              this.form.controls.memo.setValidators([Validators.required])
              this.form.controls.memo.updateValueAndValidity({
                emitEvent: false
              })
            }
            return config
          } catch (e) {
            console.error('Error parsing employee booking configuration')
            return this.employeeBookingConfig
          }
        })
      )

    this.form.valueChanges
      .pipe(untilDestroyed(this), withLatestFrom(this.productEntities$))
      .subscribe(([valueChanges, productEntities]) => {
        const f = this.form.value
        if (
          f.productId &&
          f.startDate &&
          (!f.endDate || f.startDate.valueOf() == f.endDate.valueOf())
        ) {
          this.form.controls.endDate.patchValue(
            moment(f.startDate).add(
              productEntities[f.productId]?.slotMinutes,
              'minutes'
            )
          )
        }
      })

    this.form.controls.startDate.valueChanges
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.store.select(employeeContractSelectors.selectAll))
      )
      .subscribe(([valueChanges, contracts]) => {
        if (!this.form.value.startDate) {
          return
        }
        if (
          moment(valueChanges).format('YYYY-MM-DD') !=
          (typeof this.form.value.startDate === 'string'
            ? moment(this.form.value.startDate)?.format('YYYY-MM-DD')
            : this.form.value.startDate?.format('YYYY-MM-DD'))
        ) {
          const value = moment(valueChanges)
            .set('hours', Number(this.form.value.startDate?.format('HH')))
            .set('minutes', Number(this.form.value.startDate?.format('mm')))

          this.form.controls.startDate.setValue(value, { emitEvent: false })
        }
        if (
          this.form.controls['contractId']?.value &&
          this.form.value.startDate
        ) {
          this.setDisableButton(
            contracts,
            this.form.controls['contractId']?.value
          )
        }
      })

    this.form.controls.endDate.valueChanges
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.store.select(employeeContractSelectors.selectAll))
      )
      .subscribe(([valueChanges, contracts]) => {
        if (!this.form.value.endDate) {
          return
        }
        if (
          moment(valueChanges).format('YYYY-MM-DD') !=
          (typeof this.form.value.startDate === 'string'
            ? moment(this.form.value.endDate)?.format('YYYY-MM-DD')
            : this.form.value.endDate?.format('YYYY-MM-DD'))
        ) {
          const value = moment(valueChanges)
            .set('hours', Number(this.form.value.endDate?.format('HH')))
            .set('minutes', Number(this.form.value.endDate?.format('mm')))

          this.form.controls.endDate.setValue(value, { emitEvent: false })
        }
        if (
          this.form.controls['contractId']?.value &&
          this.form.value.endDate
        ) {
          this.setDisableButton(
            contracts,
            this.form.controls['contractId']?.value
          )
        }
      })
    this.form.controls['contractId'].valueChanges
      .pipe(untilDestroyed(this), withLatestFrom(this.contracts$))
      .subscribe(([contractId, contracts]) => {
        this.setDisableButton(contracts, contractId)
      })
  }

  private updateControlEditability(controlName: string, editable: boolean) {
    const control = this.form.controls[controlName]
    if (!control) {
      return
    }
    if (editable) {
      control.enable({ emitEvent: false })
    } else {
      control.disable({ emitEvent: false })
    }
  }

  onFormSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })

      scrollToFirstInvalidControl(this.elementRef, this.notificationService)
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    this.contractEntities$.pipe(take(1)).subscribe((contractEntities) => {
      const contractValidFromDate =
        contractEntities[this.form.value.contractId]?.startDate
      const contractValidTillDate =
        contractEntities[this.form.value.contractId]?.endDate

      if (
        this.selectedType != EmployeeBookingTypes.UNAVAILABLE &&
        moment(contractValidTillDate)
          .endOf('day')
          .isBefore(moment(this.form.value.endDate))
      ) {
        this.contractIsNotValidOnEndDate = true
      } else if (
        this.selectedType != EmployeeBookingTypes.UNAVAILABLE &&
        moment(contractValidFromDate).isAfter(moment(this.form.value.startDate))
      ) {
        this.contractIsNotValidOnStartDate = true
      } else {
        this.disableSaveButton = true
        this.onSubmit.emit({
          startDate: this.employeeBooking.startDate,
          endDate: this.employeeBooking.endDate,
          type: this.employeeBooking.type,
          productId: this.employeeBooking.productId,
          contractId: this.employeeBooking.contractId,
          description: this.employeeBooking.description,
          memo: this.employeeBooking.memo,
          ...this.form.value
        })
      }
    })
  }

  public shouldShowCancelButton$(): Observable<boolean> {
    return combineLatest([
      this.store.select(employeeContractSelectors.selectEntities),
      this.store.select(employeeContractManagerLinkSelectors.selectEntities),
      this.employeeBookingConfig$
    ]).pipe(
      take(1),
      map(
        ([
          contractEntities,
          employeeContractManagerLinkEntities,
          employeeBookingConfig
        ]) => {
          if (employeeBookingConfig.cancelBookingActive === false) {
            return false
          }
          if (this.bookingIsAlreadyChecked) {
            return false
          }
          if (this.employeeBooking?.id) {
            return this.employeeBookingService.showCancelButton(
              this.employeeBooking,
              contractEntities,
              employeeContractManagerLinkEntities
            )
          }
          return false
        }
      )
    )
  }

  public cancelEmployeeBooking() {
    if (this.employeeBooking.type != EmployeeBookingTypes.UNAVAILABLE) {
      if (!this.showConfirm) {
        this.showConfirm = true
        return
      }
    } else {
      const message = this.translate.instant('bookingModule.deleteBooking')
      if (!window.confirm(message)) {
        return
      }
    }

    this.store.dispatch(
      employeeBookingActions.updateOneById({
        id: this.employeeBooking.id,
        params: {
          id: this.employeeBooking.id,
          action: 'delete',
          customCancelMessage: this.messageForm.controls.message.value
        }
      })
    )

    this.actions$
      .pipe(
        ofType(employeeBookingActions.upsertMany),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }

        this.store.dispatch(
          employeeBookingActions.removeOne({ id: this.employeeBooking.id })
        )

        this.notificationService.emit('alerts.success', 'success')
        this.disableSaveButton = true
        this.onCancel.emit(true)
      })
  }

  private setDisableButton(contracts, contractId) {
    const contract = contracts.find((it) => it.id === contractId)
    if (contract && (contract.agendaCloseDate || contract.checkedTillDate)) {
      const agendaCloseDate = contract.agendaCloseDate
        ? moment(contract.agendaCloseDate).startOf('day').toDate().getTime()
        : -Infinity
      const checkedTillDate = contract.checkedTillDate
        ? moment(contract.checkedTillDate).startOf('day').toDate().getTime()
        : -Infinity
      const maxDate = new Date(Math.max(agendaCloseDate, checkedTillDate))
      if (maxDate.getTime() !== -Infinity) {
        const startDate = this.form.controls['startDate'].value
        const endDate = this.form.controls['endDate'].value
        const startDateTime = moment(startDate)
          .startOf('day')
          .toDate()
          .getTime()
        const endDateTime = moment(endDate).startOf('day').toDate().getTime()
        if (
          startDateTime < maxDate.getTime() ||
          endDateTime < maxDate.getTime()
        ) {
          if (this.disableSaveButton !== true) {
            this.notificationService.emit(
              'employeeModule.warningAgendaClose',
              'warning'
            )
          }
          this.disableSaveButton = true
        } else {
          this.disableSaveButton = false
        }
      } else {
        this.disableSaveButton = false
      }
    } else {
      this.disableSaveButton = false
    }
  }
}
